import React from 'react';
import { Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Body, Button } from '@cognite/cogs.js';
import styled from 'styled-components/macro';

import { Rule, Ruleset } from 'types/dt-graphql-apollo-v2';

import { getRulesetRules, term } from 'utils/project-ruleset';
import { TableContainer, LabelListDisplay } from 'components';
import { PAGINATION_PAGE_SIZE } from 'utils/config';
import { RulesetOptions } from './RulesetOptions';
import { RuleList } from './RuleList';

type RulesetListProps = {
  onDeleteRuleComplete: VoidFunction;
  onDeleteRulesetComplete: VoidFunction;
  onEditRule: (rule: Rule) => void;
  onEditRuleset: (rulesets: Ruleset) => void;
  onViewRuleset: (rulesets: Ruleset) => void;
  rules: Rule[];
  rulesets: Ruleset[];
  showPagination: boolean;
};

export const RulesetList = ({
  onDeleteRuleComplete,
  onDeleteRulesetComplete,
  onEditRule,
  onEditRuleset,
  onViewRuleset,
  rules,
  rulesets,
  showPagination,
}: RulesetListProps) => {
  const renderRulesetOptionsMenu = (ruleset: Ruleset) => (
    <RulesetOptions
      ruleset={ruleset}
      onEditRuleset={onEditRuleset}
      onDeleteRulesetComplete={onDeleteRulesetComplete}
    />
  );

  const renderRulesCol = (ruleset: Ruleset) => {
    const rulesetRules = getRulesetRules(ruleset.externalId, rules);
    const rulesCount = rulesetRules.length;

    return (
      <Body level={2}>
        {`${rulesCount} ${rulesCount === 1 ? term.rule : term.rules}`}
      </Body>
    );
  };

  const columns: ColumnsType<Ruleset> = [
    {
      key: 'name',
      render: (record: Ruleset) => (
        <StyledBody level={1}>{record.name}</StyledBody>
      ),
    },
    {
      key: 'rules',
      render: renderRulesCol,
    },
    {
      key: 'tags',
      render: (record: Ruleset) => (
        <LabelListDisplay
          labels={record.labels?.map((label) => ({
            text: label,
            key: label,
          }))}
        />
      ),
    },
    {
      key: 'options',
      render: renderRulesetOptionsMenu,
    },
  ];

  return (
    <TableContainer>
      <Table<Ruleset>
        rowKey="id"
        showHeader={false}
        dataSource={rulesets || []}
        rowClassName="row-clickable"
        onRow={(record) => ({
          onClick: () => onViewRuleset(record),
        })}
        columns={columns}
        pagination={
          showPagination
            ? { defaultPageSize: PAGINATION_PAGE_SIZE, hideOnSinglePage: true }
            : false
        }
        expandable={{
          expandIconColumnIndex: 10,
          expandedRowRender: (ruleset) => {
            const rulesetRules = getRulesetRules(ruleset.externalId, rules);

            return (
              <RuleList
                rules={rulesetRules}
                onDeleteRuleComplete={onDeleteRuleComplete}
                onEditRule={onEditRule}
                showPagination={false}
              />
            );
          },
          rowExpandable: (ruleset) => {
            const rulesetRules = getRulesetRules(ruleset.externalId, rules);

            return rulesetRules.length !== 0;
          },
          expandIcon: ({
            expanded,
            onExpand,
            record,
            expandable,
          }: {
            expanded: boolean;
            onExpand: (
              record: Ruleset,
              e: React.MouseEvent<HTMLElement>
            ) => void;
            record: Ruleset;
            expandable: boolean;
          }) => (
            <Button
              onClick={(
                e:
                  | React.MouseEvent<HTMLDivElement, MouseEvent>
                  | React.MouseEvent<HTMLElement, MouseEvent>
              ) => {
                e.stopPropagation();
                onExpand(record, e);
              }}
              icon={expanded ? 'ChevronUp' : 'ChevronDown'}
              type="ghost"
              size="small"
              arial-label={expanded ? 'Collapse row' : 'Expand row'}
              disabled={!expandable}
            />
          ),
        }}
      />
    </TableContainer>
  );
};

const StyledBody = styled(Body)`
  font-weight: 500;
`;
